import React, { useEffect, useState } from 'react';
import { Card } from '@playground/ui';
import { apiFetch } from '@/platform/api.js';

interface GameEntry {
  id: string;
  name: string;
  description: string;
  minPlayers: number;
  maxPlayers: number;
  tags: string[];
  enabled: boolean;
}

interface GameCredits {
  powerUpCosts: Record<string, number>;
  powerUpUsesPerMatch: Record<string, number>;
  rewardWin: number;
  rewardParticipation: number;
  houseEdge?: number;
  houseEdgePerBet?: Record<string, number>;
  bettingDurationMs?: number;
  resultsDurationMs?: number;
  cardDrawIntervalMs?: number;
  allowedBetAmounts?: number[];
}

const CARDS_RACING_BET_TYPES: { key: string; label: string }[] = [
  { key: 'suit_winner',            label: 'Suit winner' },
  { key: 'color_winner',           label: 'Color winner' },
  { key: 'first_card_suit',        label: 'First card suit' },
  { key: 'first_equals_winner',    label: 'First card = winner' },
  { key: 'first_retreat_suit',     label: 'First retreat suit' },
  { key: 'penalty_count',          label: 'Penalty count' },
  { key: 'photo_finish',           label: 'Photo finish' },
  { key: 'exacta',                 label: 'Exacta' },
  { key: 'trifecta',               label: 'Trifecta' },
  { key: 'margin_victory',         label: 'Margin victory' },
  { key: 'winner_first_retreated', label: 'Winner = first retreat' },
  { key: 'live_suit_winner',       label: 'Live: suit winner' },
  { key: 'live_next_card',         label: 'Live: next card' },
  { key: 'live_exacta',            label: 'Live: exacta' },
];

const HOUSE_EDGE_GAMES = new Set(['moka-crash', 'bubble-rush', 'cards-racing', 'horse-racing', 'car-racing']);

const POWER_UP_LABELS: Record<string, string> = {
  skip_question: 'Skip question',
  reveal_letter: 'Reveal letter',
};

function GameDetailsModal({
  game,
  credits,
  token,
  onClose,
  onSaved,
}: {
  game: GameEntry;
  credits: GameCredits;
  token: string;
  onClose: () => void;
  onSaved: (gameId: string, updated: GameCredits) => void;
}) {
  const [costs, setCosts] = useState<Record<string, string>>(
    Object.fromEntries(Object.entries(credits.powerUpCosts).map(([k, v]) => [k, String(v)]))
  );
  const [uses, setUses] = useState<Record<string, string>>(
    Object.fromEntries(Object.entries(credits.powerUpUsesPerMatch).map(([k, v]) => [k, String(v)]))
  );
  const [rewardWin, setRewardWin] = useState(String(credits.rewardWin));
  const [rewardParticipation, setRewardParticipation] = useState(String(credits.rewardParticipation));
  const [houseEdge, setHouseEdge] = useState(String(credits.houseEdge ?? 0.92));
  const [bettingDurationMs, setBettingDurationMs] = useState(String(credits.bettingDurationMs ?? 30000));
  const [resultsDurationMs, setResultsDurationMs] = useState(String(credits.resultsDurationMs ?? 10000));
  const [allowedBetAmounts, setAllowedBetAmounts] = useState(
    (credits.allowedBetAmounts ?? [10, 25, 50, 100, 250, 500]).join(','),
  );
  const [cardDrawIntervalMs, setCardDrawIntervalMs] = useState(String(credits.cardDrawIntervalMs ?? 1500));
  const defaultPerBet = Object.fromEntries(
    CARDS_RACING_BET_TYPES.map(({ key }) => [key, String(credits.houseEdgePerBet?.[key] ?? 0.92)])
  );
  const [houseEdgePerBet, setHouseEdgePerBet] = useState<Record<string, string>>(defaultPerBet);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState('');

  const powerUpKeys = Object.keys(credits.powerUpCosts);

  async function handleSave() {
    setSaving(true);
    setSaved(false);
    try {
      const configRes = await apiFetch('/api/admin/config', { headers: { Authorization: `Bearer ${token}` } });
      const config = await configRes.json() as Record<string, unknown>;
      const existingGames = (config['games'] as Record<string, unknown>) ?? {};
      const updatedGame: Record<string, unknown> = {
        ...(existingGames[game.id] as Record<string, unknown> ?? {}),
        powerUpCosts: Object.fromEntries(Object.entries(costs).map(([k, v]) => [k, Number(v) || 0])),
        powerUpUsesPerMatch: Object.fromEntries(Object.entries(uses).map(([k, v]) => [k, Number(v) || 0])),
        rewardWin: Number(rewardWin) || 0,
        rewardParticipation: Number(rewardParticipation) || 0,
      };
      if (HOUSE_EDGE_GAMES.has(game.id)) {
        const parsed = parseFloat(houseEdge);
        updatedGame['houseEdge'] = isNaN(parsed) ? 0.92 : Math.min(1, Math.max(0.01, parsed));
      }
      if (game.id === 'cards-racing') {
        updatedGame['bettingDurationMs'] = Math.max(5000, Number(bettingDurationMs) || 30000);
        updatedGame['resultsDurationMs'] = Math.max(3000, Number(resultsDurationMs) || 10000);
        updatedGame['allowedBetAmounts'] = allowedBetAmounts
          .split(',')
          .map((s) => Number(s.trim()))
          .filter((n) => n > 0);
        updatedGame['cardDrawIntervalMs'] = Math.max(500, Number(cardDrawIntervalMs) || 1500);
        updatedGame['houseEdgePerBet'] = Object.fromEntries(
          Object.entries(houseEdgePerBet).map(([k, v]) => {
            const parsed = parseFloat(v);
            return [k, isNaN(parsed) ? 0.92 : Math.min(1, Math.max(0.01, parsed))];
          })
        );
      }
      // `apiFetch` resolves for 4xx/5xx. Without this the modal closed showing
      // the new values while the server config stayed unchanged.
      const res = await apiFetch('/api/admin/config', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...config, games: { ...existingGames, [game.id]: updatedGame } }),
      });
      if (!res.ok) throw new Error(`Save rejected (${res.status})`);
      onSaved(game.id, {
        powerUpCosts: updatedGame['powerUpCosts'] as Record<string, number>,
        powerUpUsesPerMatch: updatedGame['powerUpUsesPerMatch'] as Record<string, number>,
        rewardWin: updatedGame['rewardWin'] as number,
        rewardParticipation: updatedGame['rewardParticipation'] as number,
        ...(HOUSE_EDGE_GAMES.has(game.id) ? { houseEdge: updatedGame['houseEdge'] as number } : {}),
        ...(game.id === 'cards-racing' ? {
          bettingDurationMs: updatedGame['bettingDurationMs'] as number,
          resultsDurationMs: updatedGame['resultsDurationMs'] as number,
          cardDrawIntervalMs: updatedGame['cardDrawIntervalMs'] as number,
          allowedBetAmounts: updatedGame['allowedBetAmounts'] as number[],
          houseEdgePerBet: updatedGame['houseEdgePerBet'] as Record<string, number>,
        } : {}),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save game settings.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="rounded-2xl bg-white dark:bg-gray-800 shadow-xl max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 pt-5 pb-3 border-b border-gray-100 dark:border-gray-700">
          <p className="font-bold text-gray-900 dark:text-gray-100">{game.name}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{game.description}</p>
        </div>

        <div className="px-6 py-4 space-y-4">
          {/* Power-ups */}
          {powerUpKeys.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500 mb-3">Power-ups</p>
              {powerUpKeys.map((key) => (
                <div key={key} className="mb-3">
                  <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                    {POWER_UP_LABELS[key] ?? key}
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    <label className="flex flex-col gap-1">
                      <span className="text-xs text-gray-500 dark:text-gray-400">Cost (credits)</span>
                      <input
                        type="number" min="0"
                        value={costs[key] ?? '0'}
                        onChange={(e) => setCosts((p) => ({ ...p, [key]: e.target.value }))}
                        className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1 text-sm text-gray-900 dark:text-gray-100"
                      />
                    </label>
                    <label className="flex flex-col gap-1">
                      <span className="text-xs text-gray-500 dark:text-gray-400">Uses per match</span>
                      <input
                        type="number" min="0" placeholder="0 = ∞"
                        value={uses[key] ?? '0'}
                        onChange={(e) => setUses((p) => ({ ...p, [key]: e.target.value }))}
                        className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1 text-sm text-gray-900 dark:text-gray-100"
                      />
                    </label>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Rewards */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500 mb-3">Credits rewards</p>
            <div className="grid grid-cols-2 gap-2">
              <label className="flex flex-col gap-1">
                <span className="text-xs text-gray-500 dark:text-gray-400">Win</span>
                <input
                  type="number" min="0"
                  value={rewardWin}
                  onChange={(e) => setRewardWin(e.target.value)}
                  className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1 text-sm text-gray-900 dark:text-gray-100"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs text-gray-500 dark:text-gray-400">Participation</span>
                <input
                  type="number" min="0"
                  value={rewardParticipation}
                  onChange={(e) => setRewardParticipation(e.target.value)}
                  className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1 text-sm text-gray-900 dark:text-gray-100"
                />
              </label>
            </div>
          </div>

          {/* House edge — crash games only */}
          {HOUSE_EDGE_GAMES.has(game.id) && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500 mb-3">House edge</p>
              <label className="flex flex-col gap-1">
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Return-to-player factor (0.01 – 1.00). Lower = more house profit. Default: 0.92
                </span>
                <input
                  type="number" min="0.01" max="1.00" step="0.01"
                  value={houseEdge}
                  onChange={(e) => setHouseEdge(e.target.value)}
                  className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1 text-sm text-gray-900 dark:text-gray-100 w-28"
                />
              </label>
            </div>
          )}

          {/* Cards Racing per-bet house edge */}
          {game.id === 'cards-racing' && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500 mb-3">House edge per bet</p>
              <div className="space-y-1">
                {CARDS_RACING_BET_TYPES.map(({ key, label }) => (
                  <div key={key} className="flex items-center justify-between gap-2">
                    <span className="text-xs text-gray-600 dark:text-gray-400 flex-1 min-w-0 truncate">{label}</span>
                    <input
                      type="number" min="0.01" max="1.00" step="0.01"
                      value={houseEdgePerBet[key] ?? '0.92'}
                      onChange={(e) => setHouseEdgePerBet((p) => ({ ...p, [key]: e.target.value }))}
                      className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-0.5 text-xs text-gray-900 dark:text-gray-100 w-20 shrink-0"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Cards Racing timing settings */}
          {game.id === 'cards-racing' && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500 mb-3">Round timing</p>
              <div className="grid grid-cols-2 gap-2 mb-3">
                <label className="flex flex-col gap-1">
                  <span className="text-xs text-gray-500 dark:text-gray-400">Betting phase (ms)</span>
                  <input
                    type="number" min="5000" step="1000"
                    value={bettingDurationMs}
                    onChange={(e) => setBettingDurationMs(e.target.value)}
                    className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1 text-sm text-gray-900 dark:text-gray-100"
                  />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-xs text-gray-500 dark:text-gray-400">Results phase (ms)</span>
                  <input
                    type="number" min="3000" step="1000"
                    value={resultsDurationMs}
                    onChange={(e) => setResultsDurationMs(e.target.value)}
                    className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1 text-sm text-gray-900 dark:text-gray-100"
                  />
                </label>
              </div>
              <label className="flex flex-col gap-1">
                <span className="text-xs text-gray-500 dark:text-gray-400">Card draw interval (ms, min 500)</span>
                <input
                  type="number" min="500" step="100"
                  value={cardDrawIntervalMs}
                  onChange={(e) => setCardDrawIntervalMs(e.target.value)}
                  className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1 text-sm text-gray-900 dark:text-gray-100"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs text-gray-500 dark:text-gray-400">Allowed bet amounts (comma-separated)</span>
                <input
                  type="text"
                  value={allowedBetAmounts}
                  onChange={(e) => setAllowedBetAmounts(e.target.value)}
                  className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1 text-sm text-gray-900 dark:text-gray-100"
                />
              </label>
            </div>
          )}
        </div>

        <div className="px-6 pb-5 pt-2 flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-lg border border-gray-200 dark:border-gray-600 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={saving}
            className="flex-1 rounded-lg bg-indigo-600 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : saved ? '✓ Saved' : 'Save'}
          </button>
        </div>
        {saveError && (
          <p role="alert" className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400">
            {saveError}
          </p>
        )}
      </div>
    </div>
  );
}

export function Games({ token }: { token: string }) {
  const [games, setGames] = useState<GameEntry[]>([]);
  const [gameCredits, setGameCredits] = useState<Record<string, GameCredits>>({});
  const [saving, setSaving] = useState<string | null>(null);
  const [selectedGame, setSelectedGame] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    Promise.all([
      apiFetch('/api/admin/games', { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.json()) as Promise<{ games: GameEntry[] }>,
      apiFetch('/api/admin/config', { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.json()) as Promise<{ games?: Record<string, { powerUpCosts?: Record<string, number>; powerUpUsesPerMatch?: Record<string, number>; rewardWin?: number; rewardParticipation?: number; houseEdge?: number; houseEdgePerBet?: Record<string, number>; bettingDurationMs?: number; resultsDurationMs?: number; cardDrawIntervalMs?: number; allowedBetAmounts?: number[] }> }>,
    ])
      .then(([gamesData, configData]) => {
        setLoaded(true);
        setGames(gamesData.games ?? []);
        const credits: Record<string, GameCredits> = {};
        for (const g of gamesData.games) {
          const cfg = configData.games?.[g.id];
          credits[g.id] = {
            powerUpCosts: cfg?.powerUpCosts ?? {},
            powerUpUsesPerMatch: cfg?.powerUpUsesPerMatch ?? {},
            rewardWin: cfg?.rewardWin ?? 0,
            rewardParticipation: cfg?.rewardParticipation ?? 0,
            ...(cfg?.houseEdge !== undefined ? { houseEdge: cfg.houseEdge } : {}),
            ...(cfg?.bettingDurationMs !== undefined ? { bettingDurationMs: cfg.bettingDurationMs } : {}),
            ...(cfg?.resultsDurationMs !== undefined ? { resultsDurationMs: cfg.resultsDurationMs } : {}),
            ...(cfg?.allowedBetAmounts !== undefined ? { allowedBetAmounts: cfg.allowedBetAmounts } : {}),
            ...(cfg?.cardDrawIntervalMs !== undefined ? { cardDrawIntervalMs: cfg.cardDrawIntervalMs } : {}),
            ...(cfg?.houseEdgePerBet !== undefined ? { houseEdgePerBet: cfg.houseEdgePerBet } : {}),
          };
        }
        setGameCredits(credits);
      })
      .catch(() => setError(true));
  }, [token]);

  async function toggleGame(gameId: string, enabled: boolean) {
    setSaving(gameId);
    try {
      const configRes = await apiFetch('/api/admin/config', { headers: { Authorization: `Bearer ${token}` } });
      if (!configRes.ok) return;
      const config = await configRes.json() as Record<string, unknown>;
      const games = (config['games'] as Record<string, { enabled: boolean }>) ?? {};
      games[gameId] = { ...games[gameId], enabled };
      await apiFetch('/api/admin/config', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...config, games }),
      });
      setGames((prev) => prev.map((g) => (g.id === gameId ? { ...g, enabled } : g)));
    } finally {
      setSaving(null);
    }
  }

  if (error) return <p className="text-red-500 text-sm">Failed to load games.</p>;

  const selected = selectedGame ? games.find((g) => g.id === selectedGame) : null;

  return (
    <div>
      {selected && gameCredits[selected.id] && (
        <GameDetailsModal
          game={selected}
          credits={gameCredits[selected.id]!}
          token={token}
          onClose={() => setSelectedGame(null)}
          onSaved={(id, updated) => {
            setGameCredits((prev) => ({ ...prev, [id]: updated }));
            setSelectedGame(null);
          }}
        />
      )}

      <h2 className="mb-4 text-lg font-semibold text-gray-800 dark:text-gray-100">Games</h2>
      <div className="flex flex-col gap-3">
        {games.map((game) => (
          <Card key={game.id}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100">{game.name}</h3>
                  <span className={['rounded-full px-2 py-0.5 text-xs font-medium', game.enabled ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'].join(' ')}>
                    {game.enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{game.description}</p>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{game.minPlayers}–{game.maxPlayers} players</p>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                {/* Details button */}
                <button
                  type="button"
                  onClick={() => setSelectedGame(game.id)}
                  title="Game settings"
                  className="rounded-lg p-1.5 text-gray-500 dark:text-gray-400 hover:bg-gray-100 hover:text-indigo-600 transition-colors dark:hover:bg-gray-700"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" /><path d="M12 16v-4M12 8h.01" />
                  </svg>
                </button>

                {/* Enable/disable toggle */}
                <button
                  type="button"
                  disabled={saving === game.id}
                  onClick={() => void toggleGame(game.id, !game.enabled)}
                  aria-label={game.enabled ? `Disable ${game.name}` : `Enable ${game.name}`}
                  className={['relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500', game.enabled ? 'bg-indigo-600' : 'bg-gray-200 dark:bg-gray-600', saving === game.id ? 'opacity-50' : ''].join(' ')}
                >
                  <span className={['pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out', game.enabled ? 'translate-x-5' : 'translate-x-0'].join(' ')} />
                </button>
              </div>
            </div>
          </Card>
        ))}
        {games.length === 0 && (
          // Distinguish "still fetching" from "the API returned zero games",
          // which previously both rendered as a permanent "Loading…".
          <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
            {loaded ? 'No games are registered.' : 'Loading…'}
          </p>
        )}
      </div>
    </div>
  );
}
